import React from 'react'
import { Icon, TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from '@shopflo/ui'
import { AddressBook, PingPong } from '@phosphor-icons/react'
import { Tooltip as MuiTooltip } from '@mui/material'
import { useLocale } from 'src/lib/hooks/useLocal'
import ImageContainer from '../common/ImageContainer'
import { imagePaths } from 'src/lib/utils/constants'

export interface SegmentTypePickerProps {
  hasDynamicSegmentsPermission: boolean
  onCreateStaticSegment: () => void
  onCreateDynamicSegment: () => void
  onCreateCustomerTagSegment?: () => void
  showShopifyHighlight: boolean
}

const PAGE_CARD_CLASS =
  'flex w-full min-w-0 h-fit items-start gap-3 rounded-xl bg-white p-4 transition-all cursor-pointer shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)] hover:shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05),0_0_0_1px_#71717B_inset]'

const SidebarCardTooltip: React.FC<{ title: string; children: React.ReactElement }> = ({ title, children }) => (
  <TooltipRoot>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent skipDelay side='top' align='start' className='!w-[200px] text-wrap text-left'>
      {title}
    </TooltipContent>
  </TooltipRoot>
)

const ShopifyCardSpinBorder: React.FC<{ roundedClass: string; fillRoundedClass: string }> = ({
  roundedClass,
  fillRoundedClass
}) => (
  <span className={`pointer-events-none absolute inset-0 z-0 overflow-hidden ${roundedClass}`} aria-hidden>
    <span className='absolute left-1/2 top-1/2 w-[140%] -translate-x-1/2 -translate-y-1/2'>
      <span className='relative block w-full pt-[100%]'>
        <span className='absolute inset-0 bg-[conic-gradient(from_180deg_at_50%_50%,transparent_0deg,#33EFB0_20deg,transparent_40deg)] animate-[spin_4s_linear_infinite] motion-reduce:animate-none' />
      </span>
    </span>
    <span className={`absolute inset-px bg-white ${fillRoundedClass}`} />
    <span
      className={`absolute inset-0 ${roundedClass} shadow-[0_0_8px_0_#D0FAE5_inset]`}
    />
  </span>
)

export const DialogSegmentTypeCards: React.FC<SegmentTypePickerProps> = ({
  hasDynamicSegmentsPermission,
  onCreateStaticSegment,
  onCreateDynamicSegment,
  onCreateCustomerTagSegment,
  showShopifyHighlight
}) => {
  const { t } = useLocale()

  return (
    <TooltipProvider delayDuration={500} skipDelay>
      <div className='flex flex-row gap-2 w-full min-w-0'>
      <SidebarCardTooltip title={t('segment_creation.static_list_description')}>
        <div
          role='button'
          tabIndex={0}
          onClick={onCreateStaticSegment}
          onKeyDown={e => e.key === 'Enter' && onCreateStaticSegment()}
          className='flex-1 min-w-0 flex flex-col items-start gap-2 rounded-lg bg-white py-3 px-4 transition-all cursor-pointer shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)] hover:shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05),0_0_0_1px_#71717B_inset]'
        >
          <Icon IconComponent={AddressBook} size='sm' className='text-pink-600' iconProps={{ weight: 'duotone' }} />
          <h3 className='text-text-sm-medium text-zinc-700'>{t('segment_creation.static_list_title')}</h3>
        </div>
      </SidebarCardTooltip>

      <SidebarCardTooltip
        title={
          hasDynamicSegmentsPermission
            ? t('segment_creation.dynamic_list_description')
            : t('segment_creation.upgrade_plan_message')
        }
      >
        <div
          role='button'
          tabIndex={0}
          onClick={() => hasDynamicSegmentsPermission && onCreateDynamicSegment()}
          onKeyDown={e => e.key === 'Enter' && hasDynamicSegmentsPermission && onCreateDynamicSegment()}
          className={`flex-1 min-w-0 flex flex-col items-start gap-2 rounded-lg bg-white py-3 px-4 transition-all shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)] ${hasDynamicSegmentsPermission ? 'cursor-pointer hover:shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05),0_0_0_1px_#71717B_inset]' : 'cursor-not-allowed opacity-50'}`}
        >
          <Icon IconComponent={PingPong} size='sm' className='text-teal-600' iconProps={{ weight: 'duotone' }} />
          <h3 className='text-text-sm-medium text-zinc-700'>{t('segment_creation.dynamic_list_title')}</h3>
        </div>
      </SidebarCardTooltip>

      <SidebarCardTooltip title={t('segment_creation.shopify_tag_description')}>
        <div
          role='button'
          tabIndex={0}
          onClick={() => onCreateCustomerTagSegment?.()}
          onKeyDown={e => e.key === 'Enter' && onCreateCustomerTagSegment?.()}
          className='relative flex-1 min-w-0 flex flex-col items-start gap-2 overflow-hidden rounded-lg bg-white py-3 px-4 transition-all cursor-pointer shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)] hover:shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05),0_0_0_1px_#71717B_inset]'
        >
          {showShopifyHighlight && <ShopifyCardSpinBorder roundedClass='rounded-lg' fillRoundedClass='rounded-[7px]' />}

          <div className='relative z-10 flex w-full items-center'>
            <ImageContainer
              height={18}
              imageSource={imagePaths.SHOPIFY_LOGO}
              defaultImage={imagePaths.IMAGE_PLACEHOLDER}
              altText={'Shopify'}
            />
            <span className='absolute left-3 -top-1 flex items-center justify-center rounded-full bg-teal-500/10 px-1.5 text-text-xxs-semibold h-4 text-teal-700'>
              {t('segment_creation.new_badge')}
            </span>
          </div>
          <h3 className='relative z-10 text-text-sm-medium text-zinc-700'>
            {t('segment_creation.shopify_tag_title')}
          </h3>
        </div>
      </SidebarCardTooltip>
      </div>
    </TooltipProvider>
  )
}

export const PageSegmentTypeCards: React.FC<SegmentTypePickerProps> = ({
  hasDynamicSegmentsPermission,
  onCreateStaticSegment,
  onCreateDynamicSegment,
  onCreateCustomerTagSegment,
  showShopifyHighlight
}) => {
  const { t } = useLocale()

  return (
    <div className='flex flex-row gap-2 w-full min-w-0'>
      <div
        role='button'
        tabIndex={0}
        onClick={onCreateStaticSegment}
        onKeyDown={e => e.key === 'Enter' && onCreateStaticSegment()}
        className={PAGE_CARD_CLASS}
      >
        <Icon IconComponent={AddressBook} size='md' className='shrink-0 text-pink-600' iconProps={{ weight: 'duotone' }} />
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <h3 className='text-text-sm-medium text-zinc-600'>{t('segment_creation.static_list_title')}</h3>
          <p className='text-text-xs-regular text-zinc-500'>{t('segment_creation.static_list_description')}</p>
        </div>
      </div>

      <MuiTooltip title={!hasDynamicSegmentsPermission ? t('segment_creation.upgrade_plan_message') : ''}>
        <div
          role='button'
          tabIndex={0}
          onClick={() => hasDynamicSegmentsPermission && onCreateDynamicSegment()}
          onKeyDown={e => e.key === 'Enter' && hasDynamicSegmentsPermission && onCreateDynamicSegment()}
          className={`${PAGE_CARD_CLASS} ${hasDynamicSegmentsPermission ? '' : 'cursor-not-allowed opacity-50 hover:shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]'}`}
        >
          <Icon IconComponent={PingPong} size='md' className='shrink-0 text-teal-600' iconProps={{ weight: 'duotone' }} />
          <div className='flex min-w-0 flex-1 flex-col gap-1'>
            <h3 className='text-text-sm-medium text-zinc-600'>{t('segment_creation.dynamic_list_title')}</h3>
            <p className='text-text-xs-regular text-zinc-500'>{t('segment_creation.dynamic_list_description')}</p>
          </div>
        </div>
      </MuiTooltip>

      <div
        role='button'
        tabIndex={0}
        onClick={() => onCreateCustomerTagSegment?.()}
        onKeyDown={e => e.key === 'Enter' && onCreateCustomerTagSegment?.()}
        className={`${PAGE_CARD_CLASS} relative overflow-hidden`}
      >
        {showShopifyHighlight && <ShopifyCardSpinBorder roundedClass='rounded-xl' fillRoundedClass='rounded-[11px]' />}

        <span className='relative z-10 flex size-6 shrink-0 items-center justify-center'>
          <ImageContainer
            height={22}
            width={22}
            imageSource={imagePaths.SHOPIFY_LOGO}
            defaultImage={imagePaths.IMAGE_PLACEHOLDER}
            altText={'Shopify'}
          />
        </span>
        <div className='relative z-10 flex min-w-0 flex-1 flex-col gap-1'>
          <div className='flex items-center gap-1'>
            <h3 className='text-text-sm-medium text-zinc-600'>{t('segment_creation.shopify_tag_title')}</h3>
            <span className='flex h-4 items-center justify-center rounded-full bg-teal-500/10 px-1.5 text-text-xxs-semibold text-teal-700'>
              {t('segment_creation.new_badge')}
            </span>
          </div>
          <p className='text-text-xs-regular text-zinc-500'>{t('segment_creation.shopify_tag_description')}</p>
        </div>
      </div>
    </div>
  )
}
